import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { backendService } from "@/services/backend.service";
import type { HubSpotCredentialUploadResponse } from "@/utils/models";

export const useHubSpotCredentialUpload = () => {
	const {
		chosenDepartment: { department_id: departmentId },
	} = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();

	const mutation = useMutation({
		mutationFn: async (file: File) => {
			track("hubspot_credential_upload_start", { departmentId });

			const { data } = await backendService.uploadHubSpotCredentials(departmentId, file);
			return data;
		},
		onSuccess: async (data: HubSpotCredentialUploadResponse) => {
			if (data.success) {
				track("hubspot_credential_upload_success", {
					departmentId,
					credentials_created: data.credentials_created,
				});
				// Invalidate all credential-related queries
				await queryClient.invalidateQueries({ queryKey: ["nurseCredentials"] });
				await queryClient.invalidateQueries({ queryKey: ["locationCredentials"] });
				showSnackbar(t("hubspot.upload.success"), "success");
			} else {
				track("hubspot_credential_upload_unmatched", {
					departmentId,
					unmatched_staff_count: data.unmatched_staff.length,
					unmatched_locations_count: data.unmatched_locations.length,
				});
			}
		},
		onError: (error: AxiosError<{ detail: string | { message: string } }>) => {
			trackError("hubspot_credential_upload_error", { error_message: error?.message });
			const detail = error?.response?.data?.detail;
			const errorMessage = typeof detail === "string" ? detail : detail?.message || t("hubspot.upload.error");
			showSnackbar(errorMessage, "error");
		},
	});

	return {
		uploadHubSpotCredentials: mutation.mutateAsync,
		isUploading: mutation.isPending,
		uploadResult: mutation.data,
		resetUpload: mutation.reset,
	};
};
